// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { convertJapaneseNumber } from "../../utils/japaneseNumbers";
import { useLocalizedPath } from "../../i18n/localizedPath";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import PracticeProgress from "../PracticeProgress/PracticeProgress";
import ProgressGate from "../ProgressGate/ProgressGate";
import StrokeOrderDiagram from "../StrokeOrderDiagram/StrokeOrderDiagramComponent";
import SpeakButton from "../SpeakButton/SpeakButtonComponent";
import { allKanjiMeanings } from "../../consts/allKanjiMeanings";
import { localizeKanjiMeaning, useKanjiMeaning } from "../../i18n/localizedKanji";
import { kanjiDetails } from "../../consts/kanjiDetails";
import { kanjiCategoryDefinitions, kanjiCategorySymbolSets } from "../../consts/kanjiCategories";
import { cleanReadingForSpeech } from "../../utils/speak";
import { assessmentLevel, kanjiStudyScope } from "../../utils/kanjiStudyScope";
import { kanaToRomaji } from "../../utils/kanaToRomaji";
import { stripRomajiApostrophes } from "../../utils/romajiApostrophe";
import { useKanjiDrawCounts } from "../../hooks/useKanjiDrawCounts";
import { useKanjiListenCounts } from "../../hooks/useKanjiListenCounts";
import { useJlptLevel } from "../../hooks/useJlptLevel";
import { useAuth } from "../../context/AuthContext";
import Seo from "../Seo/SeoComponent";
import * as analytics from "../../utils/analytics";
import styles from "./kanjiWritingPracticePage.module.scss";
import BackLink from "../ui/BackLink/BackLink";

const levelKeys = ["5", "4", "3", "2", "1", "other", "all"];
const categories = [{ key: "all", label: null }, ...kanjiCategoryDefinitions];

function shortMeaning(meaning) {
    return meaning.split(",")[0];
}

function filterByLevel(key) {
    if (key === "all") return allKanjiMeanings;
    if (key === "other") return allKanjiMeanings.filter((item) => item.jlptLevel == null);
    return allKanjiMeanings.filter((item) => item.jlptLevel === Number(key));
}

// A query matches a kanji by its glyph, its English or PT-PT meaning, or any of its
// readings - either typed as kana directly, or as romaji (readings in
// kanjiDetails are stored in kana, so a romaji query like "kai" is matched
// by converting each reading with the same kana->romaji logic the romaji
// keyboard converter uses, then comparing as plain substrings).
function kanjiMatchesQuery(item, query) {
    const trimmed = query.trim();
    if (!trimmed) return true;
    if (item.symbol === trimmed) return true;

    const lower = trimmed.toLowerCase();
    if (item.meaning.toLowerCase().includes(lower)) return true;
    if (localizeKanjiMeaning(item.symbol, item.meaning, "pt").toLowerCase().includes(lower)) return true;

    const details = kanjiDetails[item.symbol];
    if (!details) return false;
    const readings = [...(details.onyomi || []), ...(details.kunyomi || [])];
    return readings.some((reading) => {
        if (reading.includes(trimmed)) return true;
        const romaji = stripRomajiApostrophes(kanaToRomaji(reading.replace(/\./g, "")).toLowerCase());
        return romaji.includes(stripRomajiApostrophes(lower));
    });
}

// Search intentionally ignores the level/category filters and searches
// every kanji this app knows about - narrowing a search to whatever facet
// happens to be selected would silently hide a real match (e.g. a kanji
// tagged a different JLPT level than expected), which is exactly the kind
// of "I know it's here somewhere" frustration search is meant to solve.
function computeFilteredList(levelKey, categoryKey, query) {
    if (query.trim()) {
        return allKanjiMeanings.filter((item) => kanjiMatchesQuery(item, query));
    }
    let list = filterByLevel(levelKey);
    if (categoryKey !== "all") {
        const symbols = kanjiCategorySymbolSets[categoryKey];
        list = list.filter((item) => symbols.has(item.symbol));
    }
    return list;
}

function KanjiWritingPracticePage() {
    const [params] = useSearchParams();
    const number = params.get("number");
    // Practice sequences are documented and linked from Numbers as 0-9,999, so
    // cap at 4 digits here (the helper's default is wider, for the currency
    // converter) - otherwise ?number=10000 would start a session.
    const converted = number === null ? null : convertJapaneseNumber(number, 4);
    return <KanjiWritingPracticeContent key={number ?? "browse"}
        numberResult={converted?.ok ? converted.result : null}
        invalidNumber={number !== null && !converted?.ok} />;
}

function KanjiWritingPracticeContent({ numberResult, invalidNumber }) {
    const { t, i18n } = useTranslation("kanjiWritingPracticePage");
    const kanjiMeaning = useKanjiMeaning();
    const { t: tn } = useTranslation("numbersPage");
    const lp = useLocalizedPath();
    const [sequenceIndex, setSequenceIndex] = useState(0);
    const { status } = useAuth();
    const { level: preferredLevel, syncStatus: levelSyncStatus } = useJlptLevel();
    const [levelKey, setLevelKey] = useState(() => preferredLevel || "5");
    const [categoryKey, setCategoryKey] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const userChangedLevelRef = useRef(false);

    const isPt = i18n.language === "pt";
    const currentPath = isPt ? "/pt/kanji-writing-practice" : "/kanji-writing-practice";

    const levelLabel = (key) => (key === "all" ? t("levels.all") : key === "other" ? t("levels.other") : `N${key}`);

    const filteredList = useMemo(
        () => numberResult ? Array.from(numberResult.kanji).map(symbol => allKanjiMeanings.find(item => item.symbol === symbol)) : computeFilteredList(levelKey, categoryKey, searchQuery),
        [levelKey, categoryKey, searchQuery, numberResult]
    );
    const isSearching = searchQuery.trim().length > 0;

    const [browseSelected, setSelected] = useState(filteredList[0]);
    const selected = numberResult ? filteredList[sequenceIndex] : browseSelected;
    const [autoStartQuiz, setAutoStartQuiz] = useState(false);
    const [audioStatus, setAudioStatus] = useState("");
    const readingScope = useMemo(
        () => kanjiStudyScope(selected, kanjiDetails[selected.symbol], assessmentLevel(levelKey, preferredLevel), i18n.language),
        [selected, levelKey, preferredLevel, i18n.language]
    );
    // NIH-25 scope is the source of truth for which readings are visible at
    // the learner's current level. Keep audio tied to that same list.
    const readingGroups = useMemo(() => [
        { id: "Kun", readings: readingScope.required.kunyomi },
        { id: "On", readings: readingScope.required.onyomi },
    ].filter((group) => group.readings.length > 0), [readingScope]);
    const strokeSpeakText = readingGroups[0]?.readings[0]
        ? cleanReadingForSpeech(readingGroups[0].readings[0].reading)
        : selected.symbol;
    const { counts: drawCounts, increment: incrementDrawCount, syncStatus } = useKanjiDrawCounts();
    const isProgressReady = syncStatus !== "loading";
    const drawCount = drawCounts[selected.symbol] || 0;

    let drawCountMessage = t("checkingDrawHistory");
    if (isProgressReady && drawCount > 0) {
        drawCountMessage = t("drawCount", { count: drawCount });
    } else if (isProgressReady) {
        drawCountMessage = t("noDrawsYet");
    }

    const { counts: listenCounts, increment: incrementListenCount, syncStatus: listenSyncStatus } = useKanjiListenCounts();
    const isListenProgressReady = listenSyncStatus !== "loading";
    const listenCount = listenCounts[selected.symbol] || 0;

    let listenCountMessage = t("checkingListenHistory");
    if (isListenProgressReady && listenCount > 0) {
        listenCountMessage = t("listenCount", { count: listenCount });
    } else if (isListenProgressReady) {
        listenCountMessage = t("noListensYet");
    }

    function selectFirstOf(nextList) {
        if (nextList.length > 0) setSelected(nextList[0]);
        setAutoStartQuiz(false);
    }

    function applyLevel(key) {
        setLevelKey(key);
        selectFirstOf(computeFilteredList(key, categoryKey, searchQuery));
    }

    function selectLevel(key) {
        userChangedLevelRef.current = true;
        applyLevel(key);
    }

    function selectCategory(key) {
        setCategoryKey(key);
        selectFirstOf(computeFilteredList(levelKey, key, searchQuery));
    }

    function handleSearchChange(value) {
        setSearchQuery(value);
        selectFirstOf(computeFilteredList(levelKey, categoryKey, value));
    }

    function selectTile(item) {
        setSelected(item);
        setAutoStartQuiz(true);
        setAudioStatus("");
    }

    function handlePracticeComplete(summary) {
        if (summary.totalMistakes !== 0) return;
        incrementDrawCount(selected.symbol);
        analytics.track("kanji_practised", { symbol: selected.symbol, level: levelKey });
    }

    function handlePlaySound({ readingType, reading, slow = false } = {}) {
        setAudioStatus("");
        incrementListenCount(selected.symbol);
        analytics.track("audio_played", {
            context: "kanji_writing_practice",
            symbol: selected.symbol,
            ...(readingType ? { readingType, reading, speed: slow ? "slow" : "normal" } : {}),
        });
    }

    function handlePlaybackError() {
        setAudioStatus(t("audioUnavailable"));
    }

    useEffect(() => {
        if (numberResult || userChangedLevelRef.current || levelSyncStatus === "loading") return;
        if (preferredLevel && preferredLevel !== levelKey) {
            applyLevel(preferredLevel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferredLevel, levelSyncStatus]);

    let headingLabel;
    if (isSearching) {
        headingLabel = t("searchResultsHeading", { count: filteredList.length });
    } else if (categoryKey !== "all") {
        const category = categories.find((c) => c.key === categoryKey);
        headingLabel = t("levelCategoryKanjiHeading", {
            level: levelLabel(levelKey),
            category: t(`categories.${category.key}`, { defaultValue: category.label }),
            count: filteredList.length,
        });
    } else {
        headingLabel = t("levelKanjiHeading", { level: levelLabel(levelKey), count: filteredList.length });
    }

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />
            <main className={styles.main}>
                <section className={styles.container}>
                    <BackLink to={lp("/#start-learning")} label={t("common:backLink.toPractice")} />
                    <h1 className={styles.title}>{t("title")}</h1>
                    <p className={styles.description}>{t("description")}</p>

                    {invalidNumber && <p role="alert">{tn("sequenceInvalid")} <Link to={lp("/numbers")}>{tn("back")}</Link></p>}
                    {numberResult && <section className={styles.numberSequence} aria-label={tn("order")}>
                        <h2>{tn("sequenceTitle", { number: numberResult.value, kanji: numberResult.kanji })}</h2>
                        <p lang="ja">{numberResult.hiragana} · {numberResult.romaji}</p>
                        <p role="status">{tn("step", { current: sequenceIndex + 1, total: filteredList.length })}</p>
                        <div className={styles.sequenceControls}>
                            {filteredList.map((item, index) => <button type="button" key={index}
                                aria-current={index === sequenceIndex ? "step" : undefined}
                                aria-label={`${index + 1}: ${item.symbol}`}
                                onClick={() => { setSequenceIndex(index); setAutoStartQuiz(true); }}>
                                <span lang="ja">{item.symbol}</span>
                            </button>)}
                        </div>
                        <div className={styles.sequenceControls}>
                            <button type="button" disabled={sequenceIndex === 0} onClick={() => { setSequenceIndex(index => index - 1); setAutoStartQuiz(true); }}>{tn("previous")}</button>
                            <button type="button" disabled={sequenceIndex === filteredList.length - 1} onClick={() => { setSequenceIndex(index => index + 1); setAutoStartQuiz(true); }}>{tn("next")}</button>
                            <Link to={lp(`/numbers?number=${numberResult.value}`)}>{tn("back")}</Link>
                        </div>
                    </section>}
                    {!numberResult && <>
                    <div className={styles.levelToggle}>
                        {levelKeys.map((key) => (
                            <button
                                key={key}
                                type="button"
                                className={levelKey === key ? styles.levelButtonActive : styles.levelButton}
                                onClick={() => selectLevel(key)}
                                disabled={isSearching}
                            >
                                {levelLabel(key)}
                            </button>
                        ))}
                    </div>

                    <div className={styles.categoryToggle}>
                        {categories.map((category) => (
                            <button
                                key={category.key}
                                type="button"
                                className={
                                    categoryKey === category.key ? styles.categoryButtonActive : styles.categoryButton
                                }
                                onClick={() => selectCategory(category.key)}
                                disabled={isSearching}
                            >
                                {category.key === "all" ? t("allCategories") : t(`categories.${category.key}`, { defaultValue: category.label })}
                            </button>
                        ))}
                    </div>

                    <input
                        type="search"
                        className={styles.searchInput}
                        placeholder={t("searchPlaceholder")}
                        value={searchQuery}
                        onChange={(event) => handleSearchChange(event.target.value)}
                        aria-label={t("searchAriaLabel")}
                    />

                    </>}
                    <div className={styles.practicePanel}>
                        <div className={styles.practiceHeader}>
                            <span className={styles.practiceGlyph}>{selected.symbol}</span>
                            <span className={styles.practiceMeaning}>{shortMeaning(kanjiMeaning(selected.symbol, selected.meaning))}</span>
                        </div>
                        {readingGroups.length > 0 && <div className={styles.readingAudioGroups} aria-label={t("readingAudioHeading")}>
                            {readingGroups.map(({ id, readings }) => (
                                <section key={id} className={styles.readingAudioGroup} aria-label={t(`label${id}`)}>
                                    <h2 className={styles.readingAudioGroupHeading}>{t(`label${id}`)}</h2>
                                    <div className={styles.readingAudioList}>
                                        {readings.map(({ reading, originalIndex }) => {
                                            const text = cleanReadingForSpeech(reading);
                                            return <span key={`${id}-${originalIndex}`} className={styles.readingAudio}>
                                                <span lang="ja" className={styles.readingAudioText}>{reading}</span>
                                                <SpeakButton
                                                    text={text}
                                                    label={t(`play${id}Pronunciation`, { symbol: selected.symbol, reading })}
                                                    className={styles.readingSpeakButton}
                                                    onPlay={() => handlePlaySound({ readingType: id, reading: text })}
                                                    onPlaybackError={handlePlaybackError}
                                                />
                                                <SpeakButton
                                                    text={text}
                                                    label={t(`playSlow${id}Pronunciation`, { symbol: selected.symbol, reading })}
                                                    className={styles.readingSpeakButton}
                                                    onPlay={() => handlePlaySound({ readingType: id, reading: text, slow: true })}
                                                    onPlaybackError={handlePlaybackError}
                                                    slow
                                                />
                                            </span>;
                                        })}
                                    </div>
                                </section>
                            ))}
                        </div>}
                        {audioStatus && <p className={styles.audioStatus} role="status">{audioStatus}</p>}
                        <StrokeOrderDiagram
                            key={`${sequenceIndex}-${selected.symbol}`}
                            symbol={selected.symbol}
                            speakText={strokeSpeakText}
                            // Rolled out gradually by JLPT level rather than all ~2,900
                            // kanji at once, since each one's stroke JSON needs to
                            // actually render cleanly for this to be worth turning on -
                            // N5 (79 kanji) is the first, smallest batch to verify. Keyed
                            // off the SELECTED kanji's own level, not the page's level
                            // toggle, since search/category results can mix levels.
                            previewAndDrawImmediately={Boolean(numberResult) || selected.jlptLevel === 5}
                            startQuizImmediately={autoStartQuiz}
                            onQuizComplete={handlePracticeComplete}
                            onPlaySound={handlePlaySound}
                            perfectMessage={status === "authenticated" ? t("perfectMessage") : undefined}
                        />
                        <ProgressGate message={t("logInToTrackPractice")}>
                            <PracticeProgress count={drawCount} label={t("practiceThisKanji")} />
                            <p className={styles.drawCount}>{drawCountMessage}</p>
                            <p className={styles.drawCount}>{listenCountMessage}</p>
                        </ProgressGate>
                        <Link to={lp(`/kanji/${selected.symbol}`)} className={styles.detailsLink}>
                            {t("viewDetailsFor", { symbol: selected.symbol })}
                        </Link>
                    </div>

                    {!numberResult && <div className={styles.card}>
                        <h2 className={styles.cardTitle}>{headingLabel}</h2>
                        {filteredList.length === 0 ? (
                            <p className={styles.emptyNote}>{t("noMatches")}</p>
                        ) : (
                            <div className={styles.grid}>
                                {filteredList.map((item) => (
                                    <button
                                        key={item.symbol}
                                        type="button"
                                        className={
                                            item.symbol === selected.symbol ? styles.gridTileActive : styles.gridTile
                                        }
                                        onClick={() => selectTile(item)}
                                    >
                                        <span className={styles.gridGlyph}>{item.symbol}</span>
                                        <span className={styles.gridMeaning}>{shortMeaning(kanjiMeaning(item.symbol, item.meaning))}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    }
                </section>
            </main>
            <Footer />
        </div>
    );
}

export default KanjiWritingPracticePage;
